import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LocationStrategy } from '@angular/common';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

import {
  LEARNING_HUB_CONFIG,
  LEARNING_HUB_LANGUAGES,
  LEARNING_HUB_MODULE_PATH
} from '../definitions/learning-hub.constants';
import {
  LearningHubArticle,
  LearningHubArticleMeta,
  LearningHubCategories,
  LearningHubConfig,
  LearningHubIndex,
  LearningHubLanguage
} from '../definitions/learning-hub.models';
import { resolveArticleHtml } from '../utils/learning-hub.utils';

/**
 * Loads the static Learning Hub content (generated by scripts/learning-hub/convert-wxr.py) on demand.
 * Nothing is bundled: indexes, categories and articles are fetched from `contentBaseUrl` and cached
 * in memory for the lifetime of the lazy module.
 */
@Injectable()
export class LearningHubService {
  private readonly INDEXES = new Map<LearningHubLanguage, Observable<LearningHubIndex>>();
  private readonly ARTICLES = new Map<number, Observable<LearningHubArticle>>();
  private _categories$: Observable<LearningHubCategories>;

  constructor(
    private readonly _http: HttpClient,
    private readonly _locationStrategy: LocationStrategy,
    @Inject(LEARNING_HUB_CONFIG) private readonly _config: LearningHubConfig
  ) {}

  /** Base href for internal links inside article HTML ("#/learning-hub" with hash routing). */
  get routeBase(): string {
    return this._locationStrategy.prepareExternalUrl(`/${LEARNING_HUB_MODULE_PATH}`);
  }

  getIndex(lang: LearningHubLanguage): Observable<LearningHubIndex> {
    return this._cached(this.INDEXES, lang, () => this._http.get<LearningHubIndex>(this._url(`index.${lang}.json`)));
  }

  /** Category tree (top-level sections, children, articles per language) and the "Staff Pick" of the home. */
  getCategories(): Observable<LearningHubCategories> {
    if (!this._categories$) {
      this._categories$ = this._http.get<LearningHubCategories>(this._url('categories.json')).pipe(
        catchError((error: unknown) => {
          this._categories$ = null;
          return throwError(error);
        }),
        shareReplay(1)
      );
    }
    return this._categories$;
  }

  findBySlug(lang: LearningHubLanguage, slug: string): Observable<LearningHubArticleMeta | null> {
    return this.getIndex(lang).pipe(
      map(
        ({ articles }: LearningHubIndex) =>
          articles.find((article: LearningHubArticleMeta) => article.slug === slug) || null
      )
    );
  }

  /** Looks for a slug in the preferred language first and then in the other ones (old root URLs). */
  findBySlugInAnyLanguage(slug: string, preferred: LearningHubLanguage): Observable<LearningHubArticleMeta | null> {
    const order = [preferred, ...LEARNING_HUB_LANGUAGES.filter((lang: LearningHubLanguage) => lang !== preferred)];
    const search = (position: number): Observable<LearningHubArticleMeta | null> =>
      position >= order.length
        ? of(null)
        : this.findBySlug(order[position], slug).pipe(
          switchMap((found: LearningHubArticleMeta | null) => (found ? of(found) : search(position + 1)))
        );
    return search(0);
  }

  getArticle(id: number): Observable<LearningHubArticle> {
    return this._cached(this.ARTICLES, id, () =>
      this._http.get<LearningHubArticle>(this._url(`articles/${id}.json`)).pipe(
        map((article: LearningHubArticle) => ({
          ...article,
          html: resolveArticleHtml(article.html, { mediaBaseUrl: this._config.mediaBaseUrl, routeBase: this.routeBase })
        }))
      )
    );
  }

  private _url(file: string): string {
    return `${this._config.contentBaseUrl.replace(/\/+$/, '')}/${file}`;
  }

  private _cached<K, T>(cache: Map<K, Observable<T>>, key: K, load: () => Observable<T>): Observable<T> {
    if (!cache.has(key)) {
      cache.set(
        key,
        load().pipe(
          catchError((error: unknown) => {
            cache.delete(key);
            return throwError(error);
          }),
          shareReplay(1)
        )
      );
    }
    return cache.get(key);
  }
}
